import BestItinerariesBuilder from './BestItinerariesBuilder';
import HighlightedItinerariesBuilder from './HighlightedItinerariesBuilder';
import transportTypePredicate from '@components/Filters/Components/TransportTypeFilter/queries/predicate';
import timeRangesPredicate from '@components/Filters/Components/TimeFilter/queries/predicate';
import maxDurationsPredicate from '@components/Filters/Components/StopDurationFilter/queries/predicate';
import hasPrimeDayPredicate from '@components/Filters/Components/PrimeDayFilter/queries/predicate';
import hasPinnedSegmentsPredicate from '@components/Results/Components/PinSegment/pinSegmentQueries/predicate';
import hasCarbonFootprintPredicate from '@components/Filters/Components/CarbonFootprintFilter/queries/predicate';
import hasPriceFilterPredicate from '@components/Filters/Components/PriceFilter/queries/predicate';
import hasMonthPredicate from '@components/SEM/Filters/SemMonthFilter/semMonthFilterQueries/predicate';
import hasWeekDayPredicate from '@components/SEM/Filters/SemWeekDayFilter/semWeekDayFilterQueries/predicate';
import hasDurationRangePredicate from '@components/Filters/Components/DurationFilter/durationFilterQueries/predicate';

const buildPredicate = ({
  transportTypes,
  timeRanges,
  maxDurations,
  connectionThresholdPredicate,
  connectionsPredicate,
  airportsPredicate,
  carriersPredicate,
  pinnedSegmentIds,
  aircraftPredicate,
  isPrimeDayFare,
  isEco,
}) => {
  const predicates = [
    transportTypePredicate(transportTypes),
    timeRangesPredicate(timeRanges),
    maxDurationsPredicate(maxDurations),
    connectionThresholdPredicate,
    connectionsPredicate,
    airportsPredicate,
    carriersPredicate,
    hasPrimeDayPredicate(isPrimeDayFare),
    hasPinnedSegmentsPredicate(pinnedSegmentIds),
    hasCarbonFootprintPredicate(isEco),
    aircraftPredicate,
  ];

  return (itinerary) => predicates.every((predicate) => predicate(itinerary));
};

const DEFAULT_HIGHLIGHTED_TYPE = 'default';

const normalizeItinerariesData = (itinerariesData) =>
  itinerariesData.reduce((itineraries, { highlightedType, itinerary }) => {
    if (itinerary) {
      itineraries[highlightedType] = itinerary;
    }
    return itineraries;
  }, {});

const countWithoutProduct = (itineraries, product) => {
  return itineraries?.filter((itinerary) => {
    return itinerary.legs.every((leg) => {
      return leg.segments.every((segment) => {
        return !segment.transportTypes.includes(product);
      });
    });
  }).length;
};

const FilteredDataBuilder = (sortedData, filterRequest) => {
  const bestItinerariesBuilder = BestItinerariesBuilder();
  const predicate = buildPredicate(filterRequest);
  const filteredItineraries = [];
  let position = 0;
  const totalItinerariesByProduct = {
    PLANE: countWithoutProduct(sortedData.itineraries, 'TRAIN'),
    TRAIN: countWithoutProduct(sortedData.itineraries, 'PLANE'),
  };
  return {
    add(itinerary) {
      if (predicate(itinerary)) {
        bestItinerariesBuilder.add(itinerary, position);
        filteredItineraries.push({
          ...itinerary,
          highlightedType: DEFAULT_HIGHLIGHTED_TYPE,
        });
        ++position;
      }
    },
    build() {
      const bestItinerariesData = bestItinerariesBuilder.build();
      const highlightedItinerariesData =
        HighlightedItinerariesBuilder(bestItinerariesData).build();

      highlightedItinerariesData.forEach(({ highlightedType, position }) => {
        filteredItineraries[position].highlightedType = highlightedType;
      });

      const bestItineraries = normalizeItinerariesData(bestItinerariesData);
      const highlightedItineraries = normalizeItinerariesData(
        highlightedItinerariesData
      );
      return {
        ...sortedData,
        itineraries: filteredItineraries,
        bestItineraries,
        highlightedItineraries,
        filteredItinerariesNumber: filteredItineraries.length,
        totalItinerariesNumber: sortedData.itineraries.length,
        totalItinerariesByProduct,
      };
    },
  };
};

const buildSemPredicate = ({
  transportTypes,
  timeRanges,
  maxDurations,
  connectionThresholdPredicate,
  connectionsPredicate,
  airportsPredicate,
  carriersPredicate,
  isPrimeDayFare,
  priceRange,
  monthsSelected,
  weekDaysSelected,
  durationRangeSelected,
}) => {
  const predicates = [
    transportTypePredicate(transportTypes),
    timeRangesPredicate(timeRanges),
    maxDurationsPredicate(maxDurations),
    connectionThresholdPredicate,
    connectionsPredicate,
    airportsPredicate,
    carriersPredicate,
    hasPrimeDayPredicate(isPrimeDayFare),
    hasPriceFilterPredicate(priceRange),
    hasMonthPredicate(monthsSelected),
    hasWeekDayPredicate(weekDaysSelected),
    hasDurationRangePredicate(durationRangeSelected),
  ];
  return (itinerary) => predicates.every((predicate) => predicate(itinerary));
};

export const SemFilteredDataBuilder = (sortedData, filterRequest) => {
  const predicate = buildSemPredicate(filterRequest);
  const filteredItineraries = [];
  return {
    add(itinerary) {
      if (predicate(itinerary)) {
        filteredItineraries.push({
          ...itinerary,
        });
      }
    },
    build() {
      return {
        ...sortedData,
        itineraries: filteredItineraries,
        filteredItinerariesNumber: filteredItineraries.length,
        totalItinerariesNumber: sortedData.itineraries.length,
      };
    },
  };
};

export default FilteredDataBuilder;
